import React, { useState, useEffect, useMemo } from 'react';
import { SEOHead } from '../components/common/SEOHead';
import { Breadcrumbs } from '../components/common/Breadcrumbs';
import { CategoryCard } from '../components/common/CategoryCard';
import { ProductCard } from '../components/common/ProductCard';
import { BrandCard } from '../components/common/BrandCard';
import { LeadFormDialog } from '../components/common/LeadFormDialog';
import { getCategories, getProducts, getBrands } from '../lib/api';
import { dedupeBrandsBySlug } from '../lib/utils';
import { Search, Filter, X } from 'lucide-react';
import { Input } from '../components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';

const BRAND_GRID_ROWS_COLLAPSED = 3;

function getBrandGridColumnCount() {
  if (typeof window === 'undefined') return 3;
  if (window.matchMedia('(min-width: 1024px)').matches) return 8;
  if (window.matchMedia('(min-width: 768px)').matches) return 6;
  if (window.matchMedia('(min-width: 640px)').matches) return 4;
  return 3;
}

export default function ProductsPage() {
  const [categories, setCategories] = useState([]);
  const [brands, setBrands] = useState([]);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showQuoteForm, setShowQuoteForm] = useState(false);

  // Filters
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [selectedBrand, setSelectedBrand] = useState('all');
  const [showFilters, setShowFilters] = useState(false);
  const [showAllBrands, setShowAllBrands] = useState(false);
  const [brandGridCols, setBrandGridCols] = useState(() =>
    typeof window !== 'undefined' ? getBrandGridColumnCount() : 3,
  );

  const uniqueBrands = useMemo(() => dedupeBrandsBySlug(brands), [brands]);
  const brandPreviewCount = brandGridCols * BRAND_GRID_ROWS_COLLAPSED;
  const brandsToDisplay =
    showAllBrands || uniqueBrands.length <= brandPreviewCount
      ? uniqueBrands
      : uniqueBrands.slice(0, brandPreviewCount);
  const canToggleBrands = uniqueBrands.length > brandPreviewCount;

  useEffect(() => {
    const updateCols = () => setBrandGridCols(getBrandGridColumnCount());
    updateCols();
    window.addEventListener('resize', updateCols);
    return () => window.removeEventListener('resize', updateCols);
  }, []);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [catRes, brandRes] = await Promise.all([
          getCategories(),
          getBrands(),
        ]);
        setCategories(catRes.data);
        setBrands(brandRes.data);

        const pageSize = 5000;
        let skip = 0;
        const merged = [];
        for (;;) {
          const prodRes = await getProducts({ limit: pageSize, skip });
          const batch = prodRes.data || [];
          merged.push(...batch);
          if (batch.length < pageSize) break;
          skip += pageSize;
        }
        setProducts(merged);
      } catch (error) {
        console.error('Error fetching data:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  useEffect(() => {
    setSelectedBrand('all');
  }, [selectedCategory]);

  const brandsInCategory = useMemo(() => {
    if (selectedCategory === 'all') return [];
    return brands.filter((b) => b.category_id === selectedCategory);
  }, [brands, selectedCategory]);

  // Filter products
  const filteredProducts = products.filter((product) => {
    const matchesSearch = product.name.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesCategory = selectedCategory === 'all' || product.category_id === selectedCategory;
    const matchesBrand =
      selectedBrand === 'all' ||
      (selectedCategory !== 'all' && product.brand_id === selectedBrand);
    if (selectedCategory === 'all') {
      return matchesSearch && matchesCategory;
    }
    return matchesSearch && matchesCategory && matchesBrand;
  });

  const clearFilters = () => {
    setSearchQuery('');
    setSelectedCategory('all');
    setSelectedBrand('all');
  };

  const hasActiveFilters = searchQuery || selectedCategory !== 'all' || selectedBrand !== 'all';

  return (
    <>
      <SEOHead
        title="Products - Premium Fragrances & Cosmetics Catalog"
        description="Browse our complete catalog of luxury perfumes, deodorants, body mists, gift sets, attars, and cosmetics. Wholesale pricing available."
        canonical="https://passionofaroma.com/products"
      />

      {/* Hero */}
      <section className="pt-32 pb-12 bg-[#0B1F3B]" data-testid="products-hero">
        <div className="container-custom">
          <Breadcrumbs items={[{ label: 'Products' }]} />
          <div className="mt-4">
            <h1 className="heading-xl text-white mb-4">Our Products</h1>
            <p className="text-gray-300 max-w-xl">
              Explore our comprehensive catalog of premium fragrances and beauty products 
              from the world's most prestigious brands.
            </p>
          </div>
        </div>
      </section>

      {/* Categories Grid */}
      <section className="py-16 bg-[#F7F6F3]" data-testid="categories-grid">
        <div className="container-custom">
          <h2 className="heading-md mb-8">Shop by Category</h2>
          {loading ? (
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
              {[...Array(6)].map((_, i) => (
                <div key={i} className="skeleton h-48" />
              ))}
            </div>
          ) : (
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
              {categories.map((cat) => (
                <CategoryCard key={cat.id} category={cat} />
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Brands */}
      <section className="py-16 bg-white" data-testid="brands-grid">
        <div className="container-custom">
          <h2 className="heading-md mb-8">Shop by Brand</h2>
          {loading ? (
            <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 lg:grid-cols-8 gap-4">
              {[...Array(brandPreviewCount)].map((_, i) => (
                <div key={i} className="skeleton h-24" />
              ))}
            </div>
          ) : (
            <>
              <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 lg:grid-cols-8 gap-4">
                {brandsToDisplay.map((brand) => (
                  <BrandCard key={brand.id} brand={brand} />
                ))}
              </div>
              {canToggleBrands && (
                <div className="flex justify-center mt-8">
                  <button
                    type="button"
                    className="btn-secondary"
                    onClick={() => setShowAllBrands((v) => !v)}
                    data-testid="brands-view-all-toggle"
                  >
                    {showAllBrands
                      ? 'Show fewer brands'
                      : `View all brands (${uniqueBrands.length})`}
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      </section>

      {/* Products Catalog */}
      <section className="py-16 bg-[#F7F6F3]" data-testid="products-catalog">
        <div className="container-custom">
          {/* Filters */}
          <div className="mb-8">
            <div className="flex flex-wrap gap-4 items-center justify-between">
              {/* Search */}
              <div className="relative w-full md:w-80">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                <Input
                  type="text"
                  placeholder="Search products..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-10 input-luxury bg-white"
                  data-testid="product-search"
                />
              </div>

              {/* Filter Toggle (Mobile) */}
              <button
                onClick={() => setShowFilters(!showFilters)}
                className="md:hidden flex items-center gap-2 btn-secondary"
              >
                <Filter className="h-4 w-4" />
                Filters
              </button>

              {/* Desktop Filters */}
              <div className="hidden md:flex gap-4">
                <Select value={selectedCategory} onValueChange={setSelectedCategory}>
                  <SelectTrigger className="w-48 bg-white" data-testid="category-filter">
                    <SelectValue placeholder="All Categories" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Categories</SelectItem>
                    {categories.map((cat) => (
                      <SelectItem key={cat.id} value={cat.id}>{cat.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>

                <Select
                  key={selectedCategory}
                  value={selectedBrand}
                  onValueChange={setSelectedBrand}
                  disabled={selectedCategory === 'all'}
                >
                  <SelectTrigger className="w-48 bg-white" data-testid="brand-filter">
                    <SelectValue
                      placeholder={
                        selectedCategory === 'all'
                          ? 'Select a category first'
                          : brandsInCategory.length
                            ? 'All brands in category'
                            : 'No brands in this category'
                      }
                    />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All brands in category</SelectItem>
                    {brandsInCategory.map((brand) => (
                      <SelectItem key={brand.id} value={brand.id}>{brand.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>

                {hasActiveFilters && (
                  <button
                    onClick={clearFilters}
                    className="flex items-center gap-1 text-sm text-gray-500 hover:text-[#D4AF37]"
                  >
                    <X className="h-4 w-4" />
                    Clear
                  </button>
                )}
              </div>
            </div>

            {/* Mobile Filters */}
            {showFilters && (
              <div className="md:hidden flex flex-col gap-4 mt-4 p-4 bg-white">
                <Select value={selectedCategory} onValueChange={setSelectedCategory}>
                  <SelectTrigger className="w-full">
                    <SelectValue placeholder="All Categories" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Categories</SelectItem>
                    {categories.map((cat) => (
                      <SelectItem key={cat.id} value={cat.id}>{cat.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>

                <Select
                  key={selectedCategory}
                  value={selectedBrand}
                  onValueChange={setSelectedBrand}
                  disabled={selectedCategory === 'all'}
                >
                  <SelectTrigger className="w-full">
                    <SelectValue
                      placeholder={
                        selectedCategory === 'all'
                          ? 'Select a category first'
                          : brandsInCategory.length
                            ? 'All brands in category'
                            : 'No brands in this category'
                      }
                    />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All brands in category</SelectItem>
                    {brandsInCategory.map((brand) => (
                      <SelectItem key={brand.id} value={brand.id}>{brand.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>

                {hasActiveFilters && (
                  <button onClick={clearFilters} className="btn-secondary">
                    Clear Filters
                  </button>
                )}
              </div>
            )}
          </div>

          {/* Results Count */}
          <p className="text-gray-500 mb-6">
            {hasActiveFilters
              ? `Showing ${filteredProducts.length} of ${products.length} products`
              : `Showing ${products.length} products`}
          </p>

          {/* Products Grid */}
          {loading ? (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
              {[...Array(8)].map((_, i) => (
                <div key={i} className="skeleton h-72" />
              ))}
            </div>
          ) : filteredProducts.length > 0 ? (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
              {filteredProducts.map((product) => {
                const category = categories.find(c => c.id === product.category_id);
                const brand = brands.find(b => b.id === product.brand_id);
                return (
                  <ProductCard
                    key={product.id}
                    product={product}
                    category={category?.name}
                    categorySlug={category?.slug}
                    brand={brand?.name}
                    brandSlug={brand?.slug}
                  />
                );
              })}
            </div>
          ) : (
            <div className="text-center py-12">
              <p className="text-gray-500 mb-4">No products found matching your criteria.</p>
              <button onClick={clearFilters} className="btn-secondary">
                Clear Filters
              </button>
            </div>
          )}

          {/* CTA */}
          <div className="mt-16 text-center p-12 bg-white">
            <h3 className="heading-md mb-4">Can't Find What You're Looking For?</h3>
            <p className="text-gray-600 mb-6 max-w-xl mx-auto">
              We have access to thousands more products. Request a custom quote and our team 
              will help you find exactly what you need.
            </p>
            <button 
              onClick={() => setShowQuoteForm(true)}
              className="btn-primary"
              data-testid="products-quote-btn"
            >
              Request Custom Quote
            </button>
          </div>
        </div>
      </section>

      <LeadFormDialog
        isOpen={showQuoteForm}
        onClose={() => setShowQuoteForm(false)}
        sourcePageUrl="/products"
        showBuyerType={true}
      />
    </>
  );
}
